import { Check, Package, Calendar, User, Camera, AlertTriangle, MapPin, Loader2, Trash2, Truck } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useGeolocation } from "@/hooks/useGeolocation";
import { toast } from "sonner";

const ROLE_STAGE_MAP = {
  contador: ["factura"],
  logistica: ["embarque"],
  chofer: ["en_ruta", "entregado"],
  ventas: [],
};

const NEXT_LABEL = {
  factura: "Listo factura",
  embarque: "Listo embarque",
  en_ruta: "Listo en ruta",
  entregado: "Marcar entregado",
};

function formatDate(iso) {
  if (!iso) return "";
  // ISO date strings (YYYY-MM-DD) are parsed as UTC midnight by the browser,
  // which shifts the displayed day by one in TZs west of UTC (e.g. America/Mexico_City).
  // Parse them as a LOCAL date instead so "2026-06-11" always shows as "11 de jun. de 2026".
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  }
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function KanbanColumn({ stage, name, items, userRole, currentUserId, onAdvance, onOpenHistory, onRouteStarted, onDelete, onReassign, mobileMode = false }) {
  const { getPosition, loading: gpsLoading } = useGeolocation();
  const [startingRouteId, setStartingRouteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const canDelete = (d) => {
    if (userRole === "master") return true;
    if (userRole === "ventas" && d.created_by === currentUserId && d.stage === "factura") return true;
    return false;
  };

  const handleDelete = async (e, d) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar la entrega para ${d.cliente}? Esta acción no se puede deshacer.`)) return;
    setDeletingId(d.id);
    try {
      await axios.delete(`${API}/deliveries/${d.id}`);
      toast.success("Entrega eliminada");
      if (onDelete) onDelete(d.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setDeletingId(null);
    }
  };

  const handleStartRoute = async (e, delivery) => {
    e.stopPropagation();
    setStartingRouteId(delivery.id);
    try {
      const pos = await getPosition();
      const res = await axios.post(`${API}/deliveries/${delivery.id}/start-route`, {
        latitude: pos.lat,
        longitude: pos.lng,
      });
      toast.success("Ruta iniciada");
      if (onRouteStarted) onRouteStarted(res.data);
    } catch (err) {
      if (err?.code === 1) toast.error("Permiso de ubicación denegado");
      else if (err?.response?.data?.detail) toast.error(err.response.data.detail);
      else toast.error("No se pudo obtener tu ubicación");
    } finally {
      setStartingRouteId(null);
    }
  };
  const canActOnStage = (ROLE_STAGE_MAP[userRole] || []).includes(stage);
  // Logística also performs local deliveries: they can act on en_ruta if the delivery
  // is assigned to an entregas_locales user.
  const canActOnDelivery = (d) => {
    if (canActOnStage) return true;
    if (userRole === "logistica" && (stage === "en_ruta") && d.assigned_driver_role === "entregas_locales") return true;
    return false;
  };

  return (
    <div className="bg-zinc-100/60 border border-zinc-200" data-testid={`column-${stage}`}>
      {/* Stage Header */}
      <div className="relative">
        <div className={`h-1 stage-bar-${stage}`} />
        <div className="p-4 flex items-center justify-between border-b border-zinc-200 bg-white">
          <div>
            <p className={`eyebrow stage-${stage}`}>{name}</p>
            <p className="font-display text-2xl font-bold mt-1">{items.length}</p>
          </div>
          <Package className="h-5 w-5 text-zinc-300" />
        </div>
      </div>

      <div className={`p-3 space-y-3 ${mobileMode ? "max-h-none" : "min-h-[300px]"}`}>
        {items.length === 0 && (
          <div className="text-center py-12 text-xs text-zinc-400">
            Sin entregas en esta etapa
          </div>
        )}

        {(() => {
          // For the en_ruta column, optionally split items by driver role so the user
          // can scan chofer vs entregas_locales deliveries quickly. Only show subheaders
          // when both groups have at least one delivery; otherwise render flat.
          let groups;
          if (stage === "en_ruta") {
            const chofer = items.filter((d) => d.assigned_driver_role !== "entregas_locales");
            const local = items.filter((d) => d.assigned_driver_role === "entregas_locales");
            if (chofer.length > 0 && local.length > 0) {
              groups = [
                { key: "chofer", label: "Chofer", items: chofer },
                { key: "entregas_locales", label: "Entregas locales", items: local },
              ];
            } else {
              groups = [{ key: "all", label: null, items }];
            }
          } else {
            groups = [{ key: "all", label: null, items }];
          }
          return groups.map((g) => (
            <div key={g.key} className="space-y-3">
              {g.label && (
                <div className="flex items-center gap-2 pt-1" data-testid={`subheader-${stage}-${g.key}`}>
                  <span className="eyebrow text-zinc-500 text-[10px]">{g.label}</span>
                  <span className="text-[10px] text-zinc-400">·</span>
                  <span className="text-[10px] text-zinc-400">{g.items.length}</span>
                  <div className="flex-1 h-px bg-zinc-200" />
                </div>
              )}
              {g.items.map((d) => {
          const todayIso = new Date().toISOString().slice(0, 10);
          const overdue = stage !== "entregado" && d.fecha_entrega < todayIso;
          return (
          <div
            key={d.id}
            data-testid={`card-${d.id}`}
            className={`bg-white border p-4 card-hover cursor-pointer relative ${overdue ? "border-rose-300" : "border-zinc-200"}`}
            onClick={() => onOpenHistory && onOpenHistory(d)}
          >
            {overdue && (
              <div className="absolute -top-px -left-px -right-px h-1 bg-rose-500" data-testid={`overdue-bar-${d.id}`} />
            )}
            <div className="flex items-start justify-between mb-3">
              <span className="eyebrow text-zinc-400">#{d.id.slice(-6)}</span>
              <div className="flex items-center gap-2">
                {overdue ? (
                  <span className="eyebrow text-rose-600 flex items-center gap-1" data-testid={`overdue-label-${d.id}`}>
                    <AlertTriangle className="h-3 w-3" /> Atrasada
                  </span>
                ) : (
                  <span className={`eyebrow stage-${stage}`}>{name}</span>
                )}
                {canDelete(d) && (
                  <button
                    onClick={(e) => handleDelete(e, d)}
                    disabled={deletingId === d.id}
                    data-testid={`btn-delete-${d.id}`}
                    title="Eliminar entrega"
                    className="p-1 -m-1 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-40"
                  >
                    {deletingId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>
            </div>

            <p className="font-display font-bold text-lg leading-tight mb-1" data-testid={`card-cliente-${d.id}`}>
              {d.cliente}
            </p>
            <p className="text-sm text-zinc-600 mb-4 flex items-center gap-1.5" data-testid={`card-herramienta-${d.id}`}>
              <Package className="h-3.5 w-3.5" /> {d.herramienta}
            </p>

            <div className="grid grid-cols-2 gap-2 text-xs mb-4">
              <div>
                <p className="eyebrow text-zinc-400 mb-1 text-[10px]">Fecha</p>
                <p className="font-medium text-zinc-800 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatDate(d.fecha_entrega)}
                </p>
              </div>
              <div>
                <p className="eyebrow text-zinc-400 mb-1 text-[10px]">Capturó</p>
                <p className="font-medium text-zinc-800 truncate flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {d.created_by_name?.split(" ")[0]}
                </p>
              </div>
            </div>

            {/* Assigned driver badge — visible on en_ruta / entregado for everyone */}
            {(stage === "en_ruta" || stage === "entregado") && d.assigned_driver_name && (
              <div className="text-[11px] text-ekin-700 border border-ekin-200 bg-ekin-50 px-2 py-1.5 mb-3 flex items-center justify-between gap-2" data-testid={`assigned-driver-${d.id}`}>
                <span className="flex items-center gap-1.5 min-w-0">
                  <Truck className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate"><strong>{d.assigned_driver_name}</strong>{d.assigned_driver_role === "entregas_locales" ? " · Entregas locales" : ""}</span>
                </span>
                {/* Reassign button (logistica + master) on en_ruta */}
                {stage === "en_ruta" && (userRole === "logistica" || userRole === "master" || userRole === "administrador") && onReassign && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onReassign(d); }}
                    data-testid={`btn-reassign-${d.id}`}
                    title="Reasignar"
                    className="text-[10px] uppercase tracking-wider underline underline-offset-2 hover:text-ekin-900 flex-shrink-0"
                  >
                    Reasignar
                  </button>
                )}
              </div>
            )}

            {d.recibido_por && stage === "entregado" && (
              <div className="text-xs text-emerald-700 border border-emerald-200 bg-emerald-50 p-2 mb-3 flex items-center justify-between" data-testid={`recibido-por-${d.id}`}>
                <span>Recibido por <strong>{d.recibido_por}</strong></span>
                {d.evidencia_path && (
                  <Camera className="h-3.5 w-3.5 text-emerald-600" data-testid={`evidencia-badge-${d.id}`} />
                )}
              </div>
            )}

            {/* GPS — start route (assigned driver OR logistica when assignment is entregas_locales) */}
            {((userRole === "chofer" || userRole === "entregas_locales") || (userRole === "logistica" && d.assigned_driver_role === "entregas_locales")) && stage === "en_ruta" && !d.route_started_at && (
              <button
                onClick={(e) => handleStartRoute(e, d)}
                disabled={startingRouteId === d.id || gpsLoading}
                data-testid={`btn-start-route-${d.id}`}
                className="btn-tactile w-full mb-2 flex items-center justify-center gap-2 border-2 border-ekin-600 text-ekin-700 py-2 text-xs font-medium uppercase tracking-wider hover:bg-ekin-50 transition-colors disabled:opacity-50"
              >
                {startingRouteId === d.id ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Obteniendo GPS...</>
                ) : (
                  <><MapPin className="h-3.5 w-3.5" /> Iniciar ruta</>
                )}
              </button>
            )}

            {/* GPS — already started indicator */}
            {stage === "en_ruta" && d.route_started_at && (
              <div className="text-[10px] text-ekin-700 border border-ekin-100 bg-ekin-50 px-2 py-1.5 mb-2 flex items-center gap-1.5" data-testid={`route-started-${d.id}`}>
                <MapPin className="h-3 w-3" /> Ruta iniciada · {new Date(d.route_started_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
              </div>
            )}

            {canActOnDelivery(d) && stage !== "entregado" && (
              <button
                onClick={(e) => { e.stopPropagation(); onAdvance(d); }}
                data-testid={`btn-listo-${stage}-${d.id}`}
                className="btn-tactile w-full flex items-center justify-center gap-2 bg-ekin-600 text-white py-2.5 text-xs font-medium uppercase tracking-wider hover:bg-ekin-700 transition-colors"
              >
                <Check className="h-3.5 w-3.5" />
                {NEXT_LABEL[stage]}
              </button>
            )}

            {stage === "entregado" && (
              <div className="w-full text-center py-2 border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-medium uppercase tracking-wider">
                Completada
              </div>
            )}
          </div>
          );
              })}
            </div>
          ));
        })()}
      </div>
    </div>
  );
}
